import { MatierePanel } from '@/components/admin/sessions/matiere/matiere-panel';
import { SessionDeleteDialog } from '@/components/admin/sessions/session-delete-dialog';
import {
  SessionFilterSummary,
  SessionFilters,
  activeSessionChips,
  hiddenSessionFilterCount,
} from '@/components/admin/sessions/session-filters';
import type {
  SessionFilterOption,
  SessionFilterOptions,
  SessionFilterPatch,
  SessionFilterState,
} from '@/components/admin/sessions/session-filters';
import { SessionsTable } from '@/components/admin/sessions/sessions-table';
import type { SessionsTab } from '@/components/admin/sessions/sessions-tabs';
import { SessionsTabs } from '@/components/admin/sessions/sessions-tabs';
import { PmsSpinner } from '@/components/ui/pms-spinner';
import { useAdminSession, useRejectOn401 } from '@/lib/admin-session';
import { fetchCriteria } from '@/lib/api';
import { isReplayFilter } from '@/lib/replay-mark';
import type { ReviewFilter } from '@/lib/review-entry';
import type { SessionListFilters } from '@/lib/session-api';
import { SESSION_DETAIL_KEY, fetchSessions, restoreSession, trashSession } from '@/lib/session-api';
import { costLabel } from '@/lib/session-labels';
import { isSessionOrigin } from '@/lib/session-origin';
import type {
  NoyauCode,
  ReplayFilter,
  SessionOrigin,
  SessionRow,
  SessionStatus,
} from '@matter_extractor/shared';
import { noyauCodeSchema, sessionDaySchema, sessionStatusSchema } from '@matter_extractor/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

/**
 * `/admin/sessions` - la liste de §12.1 et l'onglet Matière de §12.4, sous le
 * `h1` que §15 donne à l'écran.
 *
 * Elle remplace `/admin/journal`, qui redirige en gardant son `?session=`
 * (`journal.tsx`). Le renommage emporte l'adresse : c'est la décision du grill
 * du 2026-09-14, et elle se joue ici, dans le rail et dans les deux
 * redirections, jamais à moitié.
 *
 * ## Ce que cet écran demande au serveur, et pourquoi c'est trois lectures
 *
 * Le serveur filtre (§12.1), donc la liste affichée ne peut pas servir de
 * source aux OPTIONS de ses propres sélecteurs : une fois « Expérience » armée,
 * les lignes restantes n'en nomment plus qu'une, et le sélecteur n'offrirait
 * plus de quoi en changer. D'où trois lectures :
 *
 *   - la liste **sans filtre**, qui nomme les options et donne le « sur N » ;
 *   - la liste **filtrée**, qui est ce que le tableau montre ;
 *   - la **corbeille**, dont on n'a besoin que du compte tant qu'on n'y est pas.
 *
 * Elles partagent une clé de cache construite sur les filtres, donc les deux
 * qui coïncident - aucun filtre armé, ou la corbeille ouverte - ne font qu'une
 * requête. Le prix plafond est de trois, et il tombe à une sur l'écran d'arrivée.
 *
 * Une quatrième lecture, la **bibliothèque de critères**, ne sert qu'à NOMMER
 * les critères : une ligne porte `criterionIds` et rien d'autre (§12.1 ne met
 * pas huit noms de critères sur une ligne de référents). Elle partage sa clé
 * avec `/admin/criteres`, donc elle est souvent déjà en cache, et son échec ne
 * vide que ce sélecteur-là.
 *
 * ## Les dix filtres, la corbeille, l'onglet et `?session=` voyagent dans l'URL
 *
 * Le régime de toutes les listes de ce dépôt : un écran filtré est exactement
 * ce qu'on colle dans un fil d'équipe, et un rechargement ne doit pas jeter ce
 * qu'une opératrice a choisi. `validateSearch` NE LÈVE JAMAIS - un paramètre
 * inconnu dégrade vers le filtre neutre, pas vers un écran blanc.
 */

/**
 * Ce que l'écran porte dans la barre d'adresse.
 *
 * `| undefined` est épelé sur chacun : sous `exactOptionalPropertyTypes` une
 * propriété optionnelle refuse un `undefined` explicite, et « retire ce
 * filtre » EST un undefined explicite.
 *
 * Les noms sont ceux du SERVEUR (`experience`, `protocol`, …) et pas ceux de
 * l'écran (`experienceId`) : un lien collé se relit aussi dans un onglet réseau,
 * et deux orthographes pour une chose sont deux choses qui divergent.
 */
type SessionsSearch = {
  experience?: string | undefined;
  status?: SessionStatus | undefined;
  link?: string | undefined;
  from?: string | undefined;
  to?: string | undefined;
  protocol?: string | undefined;
  persona?: string | undefined;
  criterion?: string | undefined;
  /** Le noyau RETENU (noyau.md §9.2), par son triplet : le huitième filtre. */
  noyau?: NoyauCode | undefined;
  /** La relecture (relecture.md §13, #187), « avec » ou « sans » : le neuvième filtre. */
  review?: ReviewFilter | undefined;
  /** Le rejeu (rejeu.md §12, #193), « avec » ou « sans » : le dixième filtre. */
  replay?: ReplayFilter | undefined;
  /** L'origine (vitrine.md §8.2, #192), « prod » ou « dev » : le onzième filtre, visible. */
  origin?: SessionOrigin | undefined;
  /** La corbeille de §12.4 : elle REMPLACE la liste, donc elle ne voyage que vraie. */
  trash?: true | undefined;
  /** La ligne qu'un lien collé désigne. Elle marque, elle ne filtre pas. */
  session?: string | undefined;
  /** L'onglet de §15. Absent vaut « Liste », donc seul « matiere » s'écrit. */
  tab?: 'matiere' | undefined;
};

function readSearch(search: Record<string, unknown>): SessionsSearch {
  const text = (value: unknown): string | undefined => {
    const trimmed = typeof value === 'string' ? value.trim() : '';
    return trimmed === '' ? undefined : trimmed;
  };
  const day = (value: unknown): string | undefined => {
    const parsed = sessionDaySchema.safeParse(value);
    return parsed.success ? parsed.data : undefined;
  };
  const status = sessionStatusSchema.safeParse(search.status);
  const experience = text(search.experience);
  const link = text(search.link);
  const from = day(search.from);
  const to = day(search.to);
  const protocol = text(search.protocol);
  const persona = text(search.persona);
  const criterion = text(search.criterion);
  const noyau = noyauCodeSchema.safeParse(search.noyau);
  const review: ReviewFilter | undefined =
    search.review === 'with' || search.review === 'without' ? search.review : undefined;
  const replay: ReplayFilter | undefined = isReplayFilter(search.replay)
    ? search.replay
    : undefined;
  const origin: SessionOrigin | undefined = isSessionOrigin(search.origin)
    ? search.origin
    : undefined;
  const session = text(search.session);
  return {
    ...(experience === undefined ? {} : { experience }),
    ...(status.success ? { status: status.data } : {}),
    ...(link === undefined ? {} : { link }),
    ...(from === undefined ? {} : { from }),
    ...(to === undefined ? {} : { to }),
    ...(protocol === undefined ? {} : { protocol }),
    ...(persona === undefined ? {} : { persona }),
    ...(criterion === undefined ? {} : { criterion }),
    ...(noyau.success ? { noyau: noyau.data } : {}),
    ...(review === undefined ? {} : { review }),
    ...(replay === undefined ? {} : { replay }),
    ...(origin === undefined ? {} : { origin }),
    /*
      Trois orthographes acceptées pour une seule écrite. Le routeur passe
      chaque valeur par `JSON.parse`, donc ce qu'on écrit - `trash=true` -
      revient en booléen ; un lien tapé à la main dira `trash=1`, qui revient en
      NOMBRE, et un magasin de liens plus ancien pourrait porter la chaîne. Les
      trois disent la même chose, et un tiroir qui ne s'ouvrirait pas sur deux
      d'entre elles serait un lien mort sans message.
    */
    ...(search.trash === true || search.trash === 1 || search.trash === '1'
      ? { trash: true as const }
      : {}),
    ...(session === undefined ? {} : { session }),
    ...(search.tab === 'matiere' ? { tab: 'matiere' as const } : {}),
  };
}

/** L'adresse, lue comme les dix filtres. `null` y vaut « pas armé ». */
function filtersOf(search: SessionsSearch): SessionFilterState {
  return {
    experienceId: search.experience ?? null,
    status: search.status ?? null,
    linkId: search.link ?? null,
    from: search.from ?? null,
    to: search.to ?? null,
    protocolId: search.protocol ?? null,
    personaId: search.persona ?? null,
    criterionId: search.criterion ?? null,
    noyau: search.noyau ?? null,
    review: search.review ?? null,
    replay: search.replay ?? null,
    origin: search.origin ?? null,
  };
}

/** Et l'inverse, CREUX : ce qui est neutre ne s'écrit pas dans l'adresse. */
function searchOf(filters: SessionFilterState, rest: SessionsSearch): SessionsSearch {
  return {
    ...(filters.experienceId === null ? {} : { experience: filters.experienceId }),
    ...(filters.status === null ? {} : { status: filters.status }),
    ...(filters.linkId === null ? {} : { link: filters.linkId }),
    ...(filters.from === null ? {} : { from: filters.from }),
    ...(filters.to === null ? {} : { to: filters.to }),
    ...(filters.protocolId === null ? {} : { protocol: filters.protocolId }),
    ...(filters.personaId === null ? {} : { persona: filters.personaId }),
    ...(filters.criterionId === null ? {} : { criterion: filters.criterionId }),
    ...(filters.noyau === null ? {} : { noyau: filters.noyau }),
    ...(filters.review === null ? {} : { review: filters.review }),
    ...(filters.replay === null ? {} : { replay: filters.replay }),
    ...(filters.origin === null ? {} : { origin: filters.origin }),
    ...(rest.trash === true ? { trash: true as const } : {}),
    ...(rest.session === undefined ? {} : { session: rest.session }),
    ...(rest.tab === undefined ? {} : { tab: rest.tab }),
  };
}

/** Les filtres, tels que `fetchSessions` les prend. */
function apiFiltersOf(filters: SessionFilterState, trash: boolean): SessionListFilters {
  return {
    ...(filters.experienceId === null ? {} : { experienceId: filters.experienceId }),
    ...(filters.status === null ? {} : { status: filters.status }),
    ...(filters.linkId === null ? {} : { linkId: filters.linkId }),
    ...(filters.from === null ? {} : { from: filters.from }),
    ...(filters.to === null ? {} : { to: filters.to }),
    ...(filters.protocolId === null ? {} : { protocolId: filters.protocolId }),
    ...(filters.personaId === null ? {} : { personaId: filters.personaId }),
    ...(filters.criterionId === null ? {} : { criterionId: filters.criterionId }),
    ...(filters.noyau === null ? {} : { noyau: filters.noyau }),
    ...(filters.review === null ? {} : { review: filters.review }),
    ...(filters.replay === null ? {} : { replay: filters.replay }),
    ...(filters.origin === null ? {} : { origin: filters.origin }),
    ...(trash ? { trash: true } : {}),
  };
}

/**
 * La clé de cache d'une lecture, construite sur ses filtres.
 *
 * C'est elle qui fait que les trois lectures n'en sont qu'une quand elles
 * coïncident - c'est le seul endroit où cette économie se décide, et une clé
 * bâtie ailleurs la perdrait sans que rien ne le dise.
 */
function listKey(filters: SessionListFilters): string {
  return [
    filters.experienceId ?? '',
    filters.status ?? '',
    filters.linkId ?? '',
    filters.from ?? '',
    filters.to ?? '',
    filters.protocolId ?? '',
    filters.personaId ?? '',
    filters.criterionId ?? '',
    filters.noyau ?? '',
    filters.review ?? '',
    filters.replay ?? '',
    filters.origin ?? '',
    filters.trash === true ? 'corbeille' : 'vivantes',
  ].join('|');
}

/**
 * Les options des sélecteurs, lues sur les lignes NON filtrées.
 *
 * Dédupliquées par identifiant et nommées par le nom COURANT que la ligne
 * porte : une session fige une version, mais on filtre sur l'objet. Les
 * critères font exception - une ligne n'en donne que les identifiants - donc
 * leurs noms viennent de la bibliothèque, et un critère cité qu'elle ne
 * connaît plus est laissé de côté plutôt qu'offert sous son identifiant.
 */
function optionsOf(
  rows: readonly SessionRow[],
  criteria: ReadonlyMap<string, string>,
): SessionFilterOptions {
  const experiences = new Map<string, string>();
  const links = new Map<string, string>();
  const protocols = new Map<string, string>();
  const personas = new Map<string, string>();
  const cited = new Set<string>();
  for (const row of rows) {
    experiences.set(row.entry.id, row.experienceName ?? 'Expérience retirée');
    if (row.linkId !== null) links.set(row.linkId, row.linkName ?? 'Lien supprimé');
    if (row.protocolId !== null && row.protocolName !== null) {
      protocols.set(row.protocolId, row.protocolName);
    }
    if (row.personaId !== null && row.personaName !== null) {
      personas.set(row.personaId, row.personaName);
    }
    for (const id of row.criterionIds) cited.add(id);
  }
  const named = (map: ReadonlyMap<string, string>): readonly SessionFilterOption[] =>
    [...map].map(([id, name]) => ({ id, name }));
  const criterionOptions: SessionFilterOption[] = [];
  for (const id of cited) {
    const name = criteria.get(id);
    if (name !== undefined) criterionOptions.push({ id, name });
  }
  return {
    experiences: named(experiences),
    links: named(links),
    protocols: named(protocols),
    personas: named(personas),
    criteria: criterionOptions,
  };
}

const INTRO =
  'Une ligne par session : ce qu’une personne a produit en traversant une expérience. Ouvrez-en une pour relire son parcours, la conversation du protocole et le raisonnement de chaque tour.';

export const Route = createFileRoute('/admin/sessions/')({
  validateSearch: readSearch,
  component: SessionsPage,
});

function SessionsPage() {
  const { email } = useAdminSession();
  const search = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const filters = filtersOf(search);
  const trash = search.trash === true;
  const tab: SessionsTab = search.tab === 'matiere' ? 'matiere' : 'liste';

  /**
   * Le panneau « Plus de filtres » est-il ouvert ?
   *
   * De l'état de composant et PAS un paramètre d'URL : c'est une posture de
   * lecture, pas une propriété de la liste. Il s'ouvre de lui-même quand un
   * lien arrive avec un des trois filtres cachés armé - le seul cas où le
   * panneau est une information et pas du mobilier.
   */
  const [expanded, setExpanded] = useState(() => hiddenSessionFilterCount(filters) > 0);
  const [pendingDelete, setPendingDelete] = useState<SessionRow | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const shownFilters = apiFiltersOf(filters, trash);
  const sessions = useQuery({
    queryKey: ['sessions', email, listKey(shownFilters)],
    queryFn: () => fetchSessions(shownFilters),
    retry: false,
  });
  const all = useQuery({
    queryKey: ['sessions', email, listKey({})],
    queryFn: () => fetchSessions({}),
    retry: false,
  });
  const trashed = useQuery({
    queryKey: ['sessions', email, listKey({ trash: true })],
    queryFn: () => fetchSessions({ trash: true }),
    retry: false,
  });
  const criteria = useQuery({
    queryKey: ['criteria', email],
    queryFn: fetchCriteria,
    retry: false,
  });

  const rejected = useRejectOn401(sessions.error);

  /*
    DEUX clés, comme le parcours qui joue les mêmes deux gestes. La corbeille
    écrit un `deletedAt` que le serveur possède et que le bandeau du parcours
    imprime : une lecture détaillée laissée en cache montrerait, sur l'écran
    suivant, une session jetée sans son bandeau - ou restaurée avec.
  */
  const invalidate = (row: SessionRow): void => {
    void queryClient.invalidateQueries({ queryKey: ['sessions', email] });
    void queryClient.invalidateQueries({ queryKey: SESSION_DETAIL_KEY(email, row.id) });
  };

  const remove = useMutation({
    mutationFn: (row: SessionRow) => trashSession(row.id),
    onSuccess: (_answer, row) => {
      setPendingDelete(null);
      setDeleteError(null);
      invalidate(row);
    },
    onError: () => setDeleteError('La session n’a pas pu être supprimée. Réessayez.'),
  });
  const restore = useMutation({
    mutationFn: (row: SessionRow) => restoreSession(row.id),
    onSuccess: (_answer, row) => invalidate(row),
  });

  const rows = sessions.data?.sessions ?? [];
  const criterionNames = new Map(
    (criteria.data?.criteria ?? []).map((one) => [one.id, one.name] as const),
  );
  const options = optionsOf(all.data?.sessions ?? [], criterionNames);
  const chips = activeSessionChips(filters, options);
  const total = all.data?.sessions.length ?? 0;
  const cost = costLabel(rows.reduce((sum, row) => sum + row.costEur, 0));

  /**
   * Une dimension à la fois, fondue dans la barre d'adresse - et REMPLACÉE là,
   * jamais empilée.
   *
   * L'URL d'ici est un état dérivé : elle existe pour qu'une liste filtrée se
   * colle dans un fil, et un état dérivé n'a rien à faire dans la pile de
   * retour. Une pastille retirée puis remise ferait deux entrées d'historique
   * pour un aller-retour qui n'a rien déplacé.
   */
  const write = (next: SessionsSearch): void => {
    void navigate({ to: '/admin/sessions', search: next, replace: true });
  };
  const patch = (change: SessionFilterPatch): void => {
    write(searchOf({ ...filters, ...change }, search));
  };
  /**
   * Ce qui n'est pas un filtre, reconstruit à chaque écriture plutôt qu'étalé.
   *
   * `{ ...search, trash: undefined }` ne retire rien sous
   * `exactOptionalPropertyTypes` - il POSE un `undefined`, que l'adresse
   * réécrit ensuite en `trash=`. Ces trois-là s'énumèrent donc à la main, ce
   * qui est aussi la seule façon de garder l'URL creuse.
   */
  const rest = (next: { trash: boolean; tab: SessionsTab }): SessionsSearch => ({
    ...(search.session === undefined ? {} : { session: search.session }),
    ...(next.trash ? { trash: true as const } : {}),
    ...(next.tab === 'matiere' ? { tab: 'matiere' as const } : {}),
  });
  const toggleTrash = (): void => {
    /*
      La corbeille REMPLACE la liste, et elle emporte les filtres avec elle :
      revenir sur une liste vivante filtrée par une expérience après avoir
      fouillé la corbeille est exactement ce qu'on attend, donc seul `trash`
      bouge ici.
    */
    write(searchOf(filters, rest({ trash: !trash, tab })));
  };
  const selectTab = (next: SessionsTab): void => {
    write(searchOf(filters, rest({ trash, tab: next })));
  };

  /** Pourquoi la liste est vide - et c'est l'écran seul qui sait si un filtre est armé. */
  const emptyMessage = trash
    ? 'La corbeille est vide.'
    : chips.length > 0
      ? 'Aucune session ne correspond à ces filtres.'
      : 'Aucune session pour l’instant. Une session s’ouvre quand une personne entre par un lien (Réglages > Partage).';

  return (
    <section className="flex flex-col gap-5">
      <header className="flex flex-col gap-1.5">
        <h1 className="font-bold text-3xl text-bleu-nuit">Sessions</h1>
        <p className="max-w-[92ch] text-gray-600 text-sm">{INTRO}</p>
      </header>

      <SessionsTabs active={tab} onSelect={selectTab} />

      {tab === 'matiere' ? (
        /*
          Le panneau de §12.4 prend la place de la couture que ce lot avait
          posée. Il partage le `?experience=` de la liste plutôt que d'en
          ouvrir un second : c'est la MÊME question - « de quelle expérience
          parle-t-on » - et deux paramètres pour une question laisseraient un
          opérateur qui bascule d'onglet devant une autre expérience que celle
          qu'il venait de filtrer.
        */
        <MatierePanel
          experienceId={search.experience ?? null}
          onExperienceChange={(experienceId) => patch({ experienceId })}
        />
      ) : (
        <div className="flex flex-col gap-4">
          <SessionFilters
            filters={filters}
            options={options}
            expanded={expanded}
            onToggleExpanded={() => setExpanded((open) => !open)}
            onChange={patch}
          />

          <SessionFilterSummary
            chips={chips}
            shown={rows.length}
            total={total}
            cost={cost}
            trashCount={trashed.data?.sessions.length ?? 0}
            trashOpen={trash}
            onToggleTrash={toggleTrash}
            onChange={patch}
          />

          {sessions.isPending ? (
            <PmsSpinner label="Chargement des sessions" />
          ) : rejected ? null : sessions.isError ? (
            <p role="alert" className="text-rouge-deep text-sm">
              Les sessions n’ont pas pu être lues. Rechargez la page.
            </p>
          ) : rows.length === 0 ? (
            <p className="text-gray-600 text-sm">{emptyMessage}</p>
          ) : (
            <SessionsTable
              rows={rows}
              focusedId={search.session ?? null}
              trash={trash}
              onDelete={(row) => {
                setDeleteError(null);
                setPendingDelete(row);
              }}
              onRestore={(row) => restore.mutate(row)}
              restoringId={restore.isPending ? (restore.variables?.id ?? null) : null}
            />
          )}
        </div>
      )}

      {pendingDelete === null ? null : (
        <SessionDeleteDialog
          row={pendingDelete}
          busy={remove.isPending}
          error={deleteError}
          onConfirm={() => remove.mutate(pendingDelete)}
          onCancel={() => {
            setPendingDelete(null);
            setDeleteError(null);
          }}
        />
      )}
    </section>
  );
}
